import React, { useState, useEffect } from "react";
import {
  IonPage,
  IonContent,
} from "@ionic/react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Divider,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle as MuiDialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  useMediaQuery,
  useTheme as useMuiTheme,
  styled,
  Link as MuiLink,
  createTheme,
  FormHelperText,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  School,
  Refresh,
  Dashboard,
  Settings,
  ArrowBack,
  Logout,
  Search,
  GetApp,
  FilterList,
  Close as CloseIcon,
  Person,
  Assessment,
  TrendingUp,
  CheckCircle,
  Group,
  AdminPanelSettings,
  Domain,
  AccountTree,
  Add,
  Edit,
  Delete,
  People,
  Lock,
  Pin,
  Block,
  CheckCircleOutline,
  RadioButtonUnchecked,
  CloudDone,
  FileUpload,
  HowToReg,
} from "@mui/icons-material";
import { ThemeProvider } from "@emotion/react";
import axios from "axios";
import { baseUrl, config, logOutUser, getUser } from "../../common/utils";
import { useHistory, useLocation } from "react-router-dom";
import SchoolActivityStats from "./SchoolActivityStats";
import dayjs from "dayjs";

// ============================================
// Modern Dashboard Theme (MUI5) - Matching DeltaGovPortal
// ============================================
const theme = createTheme({
  palette: {
    primary: {
      main: "#243b5a",
    },
    secondary: {
      main: "#ef4444",
    },
    success: {
      main: "#059669",
    },
    error: {
      main: "#dc2626",
    },
    info: {
      main: "#3b82f6",
    },
    warning: {
      main: "#f59e0b",
    },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff",
    },
    text: {
      primary: "#1f2937",
      secondary: "#6b7280",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontSize: "1.5rem",
      fontWeight: 700,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
    },
    body1: {
      fontSize: "0.875rem",
      color: "#6b7280",
    },
    body2: {
      fontSize: "0.875rem",
      color: "#374151",
    },
    caption: {
      fontSize: "0.75rem",
      letterSpacing: "1px",
      color: "#6b7280",
      textTransform: "uppercase",
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 600,
      color: "#ffffff",
    },
  },
  shape: {
    borderRadius: 6,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: "1px solid #e5e7eb",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          borderRadius: "6px",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
            borderColor: "#243b5a",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          padding: "6px 16px",
          borderRadius: 6,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: "#f8f9fa",
          fontSize: "13px",
          color: "#4b5563",
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "#d1d5db",
          borderWidth: "1px",
        },
      },
    },
  },
});

// ============================================
// Styled Components - Matching ValidationListPage
// ============================================
const Header = styled(Box)(({ theme }) => ({
  backgroundColor: "#243b5a",
  padding: "16px 40px",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  boxSizing: "border-box",
}));

const HeaderTopRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
});

const HeaderBottomRow = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
  marginTop: "8px",
});

const LogoCircle = styled(Box)({
  width: "55px",
  height: "55px",
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  color: "#243b5a",
  fontWeight: "bold",
  border: "2px solid #f0c45a",
  boxSizing: "border-box",
  marginRight: "16px",
});

const FooterLink = styled(MuiLink)({
  color: "#d1d5db",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": {
    color: "#ffffff",
  },
});

const SchoolInfoBox = styled(Box)({
  textAlign: "right",
  color: "#f1f5f9",
});

const LogoutButton = styled(Button)({
  backgroundColor: "#ef4444",
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "6px",
  "&:hover": {
    backgroundColor: "#dc2626",
  },
  "& svg": {
    marginRight: "8px",
  },
});

// Navigation Menu Styled Components
const NavMenu = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginTop: "12px",
  flexWrap: "wrap",
});

const NavButton = styled(Button)<{ active?: boolean }>(({ active }) => ({
  color: active ? "#ffffff" : "#e0e7ff",
  backgroundColor: active ? "rgba(255,255,255,0.15)" : "transparent",
  fontWeight: active ? 600 : 500,
  fontSize: "14px",
  textTransform: "none",
  padding: "6px 16px",
  borderRadius: "20px",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.1)",
    color: "#ffffff",
  },
  "& svg": {
    marginRight: "8px",
    fontSize: "18px",
  },
}));

// Filter bar styled to match ValidationListPage
const FilterBar = styled(Paper)({
  padding: "16px 20px",
  marginBottom: "24px",
  display: "flex",
  alignItems: "center",
  gap: "16px",
  flexWrap: "wrap",
});

const SearchField = styled(TextField)({
  flex: 2,
  minWidth: "300px",
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
  },
});

const FilterSelect = styled(FormControl)({
  minWidth: '120px',
  backgroundColor: '#ffffff',
});

const ActionButton = styled(Button)({
  border: "1px solid #e5e7eb",
  color: "#374151",
  backgroundColor: "#ffffff",
  minWidth: 'auto',
  '&:hover': {
    backgroundColor: "#f9fafb",
    borderColor: "#d1d5db",
  },
});

const IconActionButton = styled(IconButton)({
  border: "1px solid #e5e7eb",
  borderRadius: "6px",
  padding: "8px",
  backgroundColor: "#ffffff",
  '&:hover': {
    backgroundColor: "#f9fafb",
  },
});

// Table styles to match ValidationListPage
const StyledTableContainer = styled(TableContainer)({
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  overflow: "hidden",
  marginTop: "16px",
});

const StyledTableHead = styled(TableHead)({
  backgroundColor: "#f8f9fa",
  '& .MuiTableCell-head': {
    fontWeight: 600,
    fontSize: "13px",
    color: "#4b5563",
    padding: "12px 16px",
    borderBottom: "1px solid #e5e7eb",
  },
});

const StyledTableCell = styled(TableCell)({
  padding: "12px 16px",
  fontSize: "14px",
  borderBottom: "1px solid #e5e7eb",
});

const StyledTableRow = styled(TableRow)({
  '&:hover': {
    backgroundColor: "#f9fafb",
  },
});

// Dialog specific styled components
const DialogHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 24px",
  borderBottom: "1px solid #e5e7eb",
});

const DialogTitleStyled = styled(Typography)({
  fontSize: "1.25rem",
  fontWeight: 600,
  color: "#243b5a",
});

const SectionTitle = styled(Typography)({
  fontSize: "1rem",
  fontWeight: 600,
  color: "#243b5a",
  marginTop: "16px",
  marginBottom: "12px",
  borderBottom: "1px solid #e5e7eb",
  paddingBottom: "8px",
});

// ============================================
// Types
// ============================================
interface School {
  id: string;
  lgaCode: string;
  lgaCode1?: string;
  schoolCode: string | number;
  schoolName: string;
  password?: string;
  accessPin?: string | null;
  blocked: string | number;
  registrationOpen: string | number;
  religiousClassification: string | number;
  progID?: number | string;
  createdAt: string;
  updatedAt: string;
}

interface SearchResult {
  id: string;
  schoolName: string;
  schoolCode: string | number;
  lgaCode: string;
  religiousClassification: string | number;
  registrationOpen: string | number;
  blocked: string | number;
  createdAt: string;
}

// ============================================
// SchoolManagement Component (Enhanced)
// ============================================
const SchoolManagement: React.FC = () => {
  const [schools, setSchools] = useState<School[]>([]);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [enableApiSearch, setEnableApiSearch] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | '', text: string }>({ type: '', text: '' });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Filters
  const [blockStatusFilter, setBlockStatusFilter] = useState<string>("");
  const [religionFilter, setReligionFilter] = useState<string>("");
  const [regStatusFilter, setRegStatusFilter] = useState<string>("");

  const [formData, setFormData] = useState<Partial<School>>({
    id: "",
    lgaCode: "",
    lgaCode1: "",
    schoolCode: "",
    schoolName: "",
    password: "",
    accessPin: "",
    blocked: "0",
    registrationOpen: "1",
    religiousClassification: "1",
    progID: "2",
    schoolType: "public"
  });

  const history = useHistory();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // Get user data from utils
  const user = getUser();

  const userData = {
    name: user?.name || "ADMIN USER",
    lgaCode: user?.lgaCode || "",
    schoolCode: user?.schoolCode || "",
  };

  useEffect(() => {
    fetchSchools();
  }, []);

  // API Search effect
  useEffect(() => {
    if (enableApiSearch && searchTerm.trim().length >= 2) {
      const timerId = setTimeout(() => {
        performApiSearch(searchTerm);
      }, 500);
      return () => clearTimeout(timerId);
    } else if (!enableApiSearch) {
      setSearchResults([]);
    }
  }, [searchTerm, enableApiSearch]);

  const fetchSchools = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${baseUrl()}Admin2s/getAll_schools`);
      console.log("Fetch Response:", response.data);
      if (response.data.status && Array.isArray(response.data.data)) {
        setSchools(response.data.data);
        setMessage({ type: "success", text: "Schools loaded successfully" });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      setMessage({ type: "error", text: "Error fetching schools" });
    } finally {
      setLoading(false);
    }
  };

  const performApiSearch = async (query: string) => {
    setSearchLoading(true);
    try {
      const response = await axios.post(`${baseUrl()}Admin2s/search_schools`, { searchTerm: query });
      console.log("Search Response:", response.data);
      if (response.data.status && Array.isArray(response.data.data)) {
        setSearchResults(response.data.data);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Search Error:", error);
      setSearchResults([]);
      setMessage({ type: "error", text: "Error searching schools" });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleToggleSearchMode = () => {
    setEnableApiSearch(!enableApiSearch);
    setSearchResults([]);
    setSearchTerm("");
  };

  // --- CRUD Logic ---
  const handleOpenAdd = () => {
    setIsEdit(false);
    setFormData({
      id: "",
      lgaCode: "",
      lgaCode1: "",
      schoolCode: "",
      schoolName: "",
      password: "",
      accessPin: "",
      blocked: "0",
      registrationOpen: "1",
      religiousClassification: "1",
      progID: "2",
      schoolType: "public"
    });
    setFormErrors({});
    setOpenModal(true);
  };

  const handleOpenEdit = (school: School) => {
    setIsEdit(true);
    // Don't include password in edit mode - it will be set separately if changed
    const { password, ...schoolWithoutPassword } = school;
    setFormData({
      ...schoolWithoutPassword,
      password: "", // Reset password field
      accessPin: school.accessPin || ""
    });
    setFormErrors({});
    setOpenModal(true);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Convert to string before checking
    const lgaCodeStr = String(formData.lgaCode || '').trim();
    const schoolCodeStr = String(formData.schoolCode || '').trim();
    const schoolNameStr = String(formData.schoolName || '').trim();

    if (!lgaCodeStr) {
      errors.lgaCode = "LGA Code is required";
    }
    if (!schoolCodeStr) {
      errors.schoolCode = "School Code is required";
    }
    if (!schoolNameStr) {
      errors.schoolName = "School Name is required";
    }
    if (!isEdit && !String(formData.password || '').trim()) {
      errors.password = "Password is required for new schools";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    // Prepare data for API - match the exact fields from the response
    const apiData: any = {
      lgaCode: formData.lgaCode,
      lgaCode1: formData.lgaCode1 || formData.lgaCode, // Use main LGA code if not provided
      schoolCode: formData.schoolCode,
      schoolName: formData.schoolName,
      religiousClassification: formData.religiousClassification,
      registrationOpen: formData.registrationOpen,
      blocked: formData.blocked,
      progID: formData.progID || "2",
      schoolType: formData.schoolType || "public",
    };

    // Only include password for new schools or if changed in edit
    if (formData.password?.trim()) {
      apiData.password = formData.password;
    }

    // Only include accessPin if provided
    if (formData.accessPin?.trim()) {
      apiData.accessPin = formData.accessPin;
    }

    // Include ID for edit
    if (isEdit && formData.id) {
      apiData.id = formData.id;
    }

    const endpoint = isEdit ? "update_schools" : "create_schools";

    try {
      const response = await axios.post(`${baseUrl()}Admin2s/${endpoint}`, apiData);
      console.log("Save Response:", response.data);

      if (response.data.status) {
        setMessage({
          type: "success",
          text: isEdit ? "School updated successfully" : "School created successfully"
        });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
        setOpenModal(false);
        fetchSchools();
        // Clear search results after successful operation
        setSearchResults([]);
        setEnableApiSearch(false);
        setSearchTerm("");
      } else {
        setMessage({ type: "error", text: response.data.message || "Operation failed" });
      }
    } catch (error) {
      console.error("Save Error:", error);
      setMessage({ type: "error", text: "Network error" });
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this school?")) {
      (async () => {
        try {
          const response = await axios.post(`${baseUrl()}Admin2s/delete_schools`, { id });
          console.log("Delete Response:", response.data);
          if (response.data.status) {
            setMessage({ type: "success", text: "School deleted successfully" });
            setTimeout(() => setMessage({ type: '', text: '' }), 3000);
            fetchSchools();
            // Refresh search results if in search mode
            if (enableApiSearch && searchTerm) {
              performApiSearch(searchTerm);
            }
          } else {
            setMessage({ type: "error", text: "Error deleting school" });
          }
        } catch (error) {
          console.error("Delete Error:", error);
          setMessage({ type: "error", text: "Network error" });
        }
      })();
    }
  };

  const handleMenuClick = (menu: string) => {
    history.push("/" + menu);
  };

  const handleLogout = () => {
    logOutUser();
    history.push("/login");
  };

  const handleRefresh = () => {
    fetchSchools();
    setEnableApiSearch(false);
    setSearchResults([]);
    setSearchTerm("");
  };

  const handleExportCSV = () => {
    console.log("Exporting CSV...");
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setBlockStatusFilter("");
    setReligionFilter("");
    setRegStatusFilter("");
    setSearchResults([]);
    setEnableApiSearch(false);
  };

  // --- Display Helpers ---
  const filteredSchools = !enableApiSearch ? (Array.isArray(schools) ? schools : []).filter(s => {
    const schoolNameMatch = s.schoolName?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
    const schoolCodeMatch = String(s.schoolCode || '').toLowerCase().includes(searchTerm.toLowerCase());
    const lgaCodeMatch = String(s.lgaCode || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSearch = searchTerm === "" || schoolNameMatch || schoolCodeMatch || lgaCodeMatch;

    const matchesBlock = blockStatusFilter === "" || String(s.blocked) === blockStatusFilter;
    const matchesReligion = religionFilter === "" || String(s.religiousClassification) === religionFilter;
    const matchesReg = regStatusFilter === "" || String(s.registrationOpen) === regStatusFilter;

    return matchesSearch && matchesBlock && matchesReligion && matchesReg;
  }) : [];

  const displaySchools = enableApiSearch ? searchResults : filteredSchools;

  const getReligiousText = (val: any) => String(val) === "1" ? "Christian" : "Islam";

  const getBlockedStatus = (val: any) => {
    const isBlocked = String(val) === "1";
    return (
      <Chip
        label={isBlocked ? "Blocked" : "Active"}
        size="small"
        sx={{
          bgcolor: isBlocked ? "#fee2e2" : "#10b981",
          color: isBlocked ? "#b91c1c" : "white",
          minWidth: 60,
        }}
      />
    );
  };

  const getRegStatus = (val: any) => {
    const s = String(val);
    if (s === "1") return <Chip label="Open" size="small" sx={{ bgcolor: "#e3f2fd", color: "#2196f3" }} />;
    if (s === "2") return <Chip label="Post-Late" size="small" sx={{ bgcolor: "#fff3e0", color: "#ed6c02" }} />;
    return <Chip label="Late" size="small" sx={{ bgcolor: "#ffebee", color: "#c62828" }} />;
  };

  // Safe formatting function for numbers
  const formatNumber = (value: string | number | undefined): string => {
    if (value === undefined || value === null) return '0';
    const num = Number(value);
    return isNaN(num) ? '0' : num.toLocaleString();
  };

  return (
    <ThemeProvider theme={theme}>
      <IonPage>
        <IonContent>
          <Box sx={{
            bgcolor: "background.default",
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}>
            {/* HEADER - Matching ValidationListPage */}
            <Header>
              <HeaderTopRow>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LogoCircle>⚖️</LogoCircle>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#cbd5e1", letterSpacing: "0.5px", display: "block" }}>
                      DELTA STATE GOVERNMENT
                    </Typography>
                    <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 600, lineHeight: 1.2 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      School Management
                    </Typography>
                  </Box>
                </Box>

                <SchoolInfoBox>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: "#ffffff", textTransform: "uppercase" }}
                  >
                    {userData.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                    LGA: {userData.lgaCode} | Code: {userData.schoolCode}
                  </Typography>
                </SchoolInfoBox>
              </HeaderTopRow>

              <HeaderBottomRow>
                <LogoutButton
                  variant="contained"
                  onClick={handleLogout}
                  startIcon={<Logout />}
                >
                  Logout
                </LogoutButton>
              </HeaderBottomRow>

              <NavMenu>
                <NavButton
                  active={location.pathname === "/admin_dashboard"}
                  onClick={() => handleMenuClick("admin_dashboard")}
                  startIcon={<Dashboard />}
                >
                  Admin Dashboard
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_students"}
                  onClick={() => handleMenuClick("admin_students")}
                  startIcon={<People />}
                >
                  Students
                </NavButton>
                <NavButton
                  active={location.pathname === "/lga_student_database"}
                  onClick={() => handleMenuClick("lga_student_database")}
                  startIcon={<Group />}
                >
                  LGA Student Database
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_schools"}
                  onClick={() => handleMenuClick("admin_schools")}
                  startIcon={<School />}
                >
                  View Schools
                </NavButton>
                <NavButton
                  active={location.pathname === "/add_result"}
                  onClick={() => handleMenuClick("add_result")}
                  startIcon={<HowToReg />}
                >
                  Add Result
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_settings"}
                  onClick={() => handleMenuClick("admin_settings")}
                  startIcon={<Settings />}
                >
                  Settings
                </NavButton>
                <NavButton
                  active={location.pathname === "/admin_management"}
                  onClick={() => handleMenuClick("admin_management")}
                  startIcon={<AdminPanelSettings />}
                >
                  Management Console
                </NavButton>

                <NavButton
                  active={location.pathname === "/admin_stats"}
                  onClick={() => handleMenuClick("admin_stats")}
                  startIcon={<Assessment />}
                >
                  Activity Stats
                </NavButton>
              </NavMenu>
            </Header>


            {/* MAIN CONTENT */}
            <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
              {/* Back Button */}
              <Button
                onClick={() => history.goBack()}
                startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                sx={{
                  color: "#243b5a",
                  fontSize: "13px",
                  textTransform: "none",
                  p: 0,
                  mb: 3,
                  '&:hover': {
                    textDecoration: "underline",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Back
              </Button>

              {/* Header with Title and Add Button */}
              <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" fontWeight={800} color="primary">School Registry</Typography>
                  <Typography color="text.secondary">Monitor and manage administrative school data.</Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleOpenAdd}
                  sx={{ bgcolor: "#243b5a" }}
                >
                  Add School
                </Button>
              </Box>

              {/* Message Alert */}
              {message.text && (
                <Alert
                  severity={message.type === 'success' ? 'success' : 'error'}
                  sx={{ mb: 3 }}
                  onClose={() => setMessage({ type: '', text: '' })}
                >
                  {message.text}
                </Alert>
              )}

              {/* Filter Bar - Exactly matching ValidationListPage style */}
              <FilterBar>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 2 }}>
                  <SearchField
                    size="small"
                    placeholder={enableApiSearch ? "Search by school name or code (API search)..." : "Search by school name, code or LGA code..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: "#9ca3af", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    disabled={enableApiSearch && searchLoading}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={enableApiSearch}
                        onChange={handleToggleSearchMode}
                        icon={<RadioButtonUnchecked />}
                        checkedIcon={<CheckCircleOutline />}
                        sx={{
                          color: "#243b5a",
                          '&.Mui-checked': {
                            color: "#243b5a",
                          },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CloudDone sx={{ fontSize: 18, color: "#243b5a" }} />
                        <Typography variant="body2" sx={{ color: "#243b5a", fontWeight: 500 }}>
                          API Search
                        </Typography>
                      </Box>
                    }
                    sx={{ ml: 1 }}
                  />
                </Box>

                <FilterSelect size="small">
                  <Select
                    value={blockStatusFilter}
                    onChange={(e) => setBlockStatusFilter(e.target.value)}
                    displayEmpty
                    disabled={enableApiSearch}
                  >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="0">Active</MenuItem>
                    <MenuItem value="1">Blocked</MenuItem>
                  </Select>
                </FilterSelect>

                <FilterSelect size="small">
                  <Select
                    value={religionFilter}
                    onChange={(e) => setReligionFilter(e.target.value)}
                    displayEmpty
                    disabled={enableApiSearch}
                  >
                    <MenuItem value="">All Religions</MenuItem>
                    <MenuItem value="1">Christian</MenuItem>
                    <MenuItem value="2">Islam</MenuItem>
                  </Select>
                </FilterSelect>

                <ActionButton
                  size="small"
                  onClick={handleClearFilters}
                  startIcon={<FilterList />}
                >
                  Clear Filters
                </ActionButton>

                <ActionButton
                  size="small"
                  onClick={handleExportCSV}
                  startIcon={<GetApp />}
                >
                  Export CSV
                </ActionButton>

                <IconActionButton
                  size="small"
                  onClick={handleRefresh}
                >
                  <Refresh />
                </IconActionButton>
              </FilterBar>

              {/* Search Status Indicator */}
              {enableApiSearch && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CloudDone sx={{ color: "#243b5a", fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    {searchLoading ? "Searching..." :
                      searchResults.length > 0 ? `Found ${searchResults.length} schools matching "${searchTerm}"` :
                        searchTerm ? `No schools found matching "${searchTerm}"` :
                          "Enter search term to find schools"}
                  </Typography>
                  {searchLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
                </Box>
              )}

              {/* Table Header Info */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {enableApiSearch
                    ? `Showing ${displaySchools.length} search results`
                    : `Showing ${filteredSchools.length} of ${schools.length} schools`
                  }
                </Typography>
              </Box>

              {/* Schools Table */}
              {loading || searchLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <StyledTableContainer component={Paper}>
                  <Table>
                    <StyledTableHead>
                      <TableRow>
                        <TableCell><b>S/N</b></TableCell>
                        <TableCell><b>School Name</b></TableCell>
                        <TableCell><b>Code</b></TableCell>
                        <TableCell><b>LGA Code</b></TableCell>
                        <TableCell align="center"><b>Classification</b></TableCell>
                        <TableCell align="center"><b>Reg. Status</b></TableCell>
                        <TableCell align="center"><b>Account</b></TableCell>
                        <TableCell><b>Created</b></TableCell>
                        <TableCell align="center"><b>Actions</b></TableCell>
                      </TableRow>
                    </StyledTableHead>
                    <TableBody>
                      {displaySchools
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((school, index) => (
                          <StyledTableRow key={school.id}>
                            <StyledTableCell>{page * rowsPerPage + index + 1}</StyledTableCell>
                            <StyledTableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: "#e3f2fd", color: "#243b5a" }}>
                                  <School sx={{ fontSize: 16 }} />
                                </Avatar>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {school.schoolName}
                                </Typography>
                              </Box>
                            </StyledTableCell>
                            <StyledTableCell>
                              <Chip
                                label={String(school.schoolCode)}
                                size="small"
                                sx={{ bgcolor: "#e3f2fd", color: "#243b5a", fontWeight: 600 }}
                              />
                            </StyledTableCell>
                            <StyledTableCell>
                              <Chip
                                label={String(school.lgaCode)}
                                size="small"
                                variant="outlined"
                                sx={{ fontWeight: 500 }}
                              />
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              <Chip
                                label={getReligiousText(school.religiousClassification)}
                                variant="outlined"
                                size="small"
                                sx={{ minWidth: 70 }}
                              />
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              {getRegStatus(school.registrationOpen)}
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              {getBlockedStatus(school.blocked)}
                            </StyledTableCell>
                            <StyledTableCell>
                              {school.createdAt ? dayjs(school.createdAt).format("DD/MM/YYYY HH:mm") : 'N/A'}
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              <IconButton
                                color="primary"
                                onClick={() => handleOpenEdit(school as School)}
                                size="small"
                                sx={{ mr: 1 }}
                                title="Edit School"
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton
                                color="error"
                                onClick={() => handleDelete(school.id)}
                                size="small"
                                title="Delete School"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}

                      {displaySchools.length === 0 && !loading && (
                        <StyledTableRow>
                          <StyledTableCell colSpan={9} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              {enableApiSearch
                                ? "No schools found matching your search"
                                : "No schools found"}
                            </Typography>
                          </StyledTableCell>
                        </StyledTableRow>
                      )}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  {displaySchools.length > 0 && (
                    <Box sx={{ p: 2, borderTop: "1px solid #e5e7eb", bgcolor: "#fafafa" }}>
                      <TablePagination
                        component="div"
                        count={displaySchools.length}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                      />
                    </Box>
                  )}
                </StyledTableContainer>
              )}
            </Container>

            {/* FOOTER - Exactly matching ValidationListPage */}
            <Box component="footer" sx={{ bgcolor: "primary.main", color: "#d1d5db", py: { xs: 5, md: 6 }, mt: "auto" }}>
              <Container maxWidth="lg">
                <Grid container spacing={4} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      MINISTRY OF PRIMARY EDUCATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Delta State Government
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Ministry of Primary Education
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Asaba, Delta State, Nigeria
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      CONTACT INFORMATION
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Email: info@dsgmope.ng
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db", mb: 0.5 }}>
                      Address: Ministry Secretariat, Asaba
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#d1d5db" }}>
                      Telephone: +234 (0)901 234 5678
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 2 }}>
                      OFFICIAL RESOURCES
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <FooterLink href="#">www.dsgmope.ng</FooterLink>
                    </Typography>
                    <Typography variant="body2">
                      <FooterLink href="#">Delta State Government Portal</FooterLink>
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 3 }} />

                <Typography variant="body2" align="center" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem" }}>
                  © 2026 Delta State Government, Ministry of Primary Education. All rights reserved.
                </Typography>
              </Container>
            </Box>

            {/* Add/Edit School Modal - Enhanced version from Component B */}
            <Dialog
              open={openModal}
              onClose={() => setOpenModal(false)}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: "8px",
                }
              }}
            >
              <DialogHeader>
                <DialogTitleStyled>
                  {isEdit ? "Edit School" : "Add New School"}
                </DialogTitleStyled>
                <IconButton onClick={() => setOpenModal(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogHeader>

              <DialogContent sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  {/* Basic Information Section */}
                  <Grid item xs={12}>
                    <SectionTitle>Basic Information</SectionTitle>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="School Name"
                      size="small"
                      required
                      value={formData.schoolName || ''}
                      onChange={e => setFormData({ ...formData, schoolName: e.target.value })}
                      error={!!formErrors.schoolName}
                      helperText={formErrors.schoolName}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="School Code"
                      size="small"
                      required
                      value={formData.schoolCode || ''}
                      onChange={e => setFormData({ ...formData, schoolCode: e.target.value })}
                      error={!!formErrors.schoolCode}
                      helperText={formErrors.schoolCode}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>School Type</InputLabel>
                      <Select
                        value={formData.schoolType || "public"}
                        label="School Type"
                        onChange={e => setFormData({ ...formData, schoolType: e.target.value })}
                      >
                        <MenuItem value="public">Public</MenuItem>
                        <MenuItem value="private">Private</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small" required>
                      <InputLabel>Program ID</InputLabel>
                      <Select
                        value={formData.progID || "2"}
                        label="Program ID"
                        onChange={e => setFormData({ ...formData, progID: e.target.value })}
                      >
                        <MenuItem value="1">Program 1</MenuItem>
                        <MenuItem value="2">Program 2</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* LGA Information Section */}
                  <Grid item xs={12}>
                    <SectionTitle>LGA Information</SectionTitle>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="LGA Code"
                      size="small"
                      required
                      value={formData.lgaCode || ''}
                      onChange={e => setFormData({ ...formData, lgaCode: e.target.value })}
                      error={!!formErrors.lgaCode}
                      helperText={formErrors.lgaCode}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Domain sx={{ fontSize: 18, color: "#9ca3af" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Additional LGA Code"
                      size="small"
                      value={formData.lgaCode1 || ''}
                      onChange={e => setFormData({ ...formData, lgaCode1: e.target.value })}
                      helperText="Optional secondary LGA code"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccountTree sx={{ fontSize: 18, color: "#9ca3af" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Configuration Section */}
                  <Grid item xs={12}>
                    <SectionTitle>Configuration</SectionTitle>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Religious Classification</InputLabel>
                      <Select
                        value={formData.religiousClassification || "1"}
                        label="Religious Classification"
                        onChange={e => setFormData({ ...formData, religiousClassification: e.target.value })}
                      >
                        <MenuItem value="1">Christian</MenuItem>
                        <MenuItem value="2">Islam</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Registration Status</InputLabel>
                      <Select
                        value={formData.registrationOpen || "1"}
                        label="Registration Status"
                        onChange={e => setFormData({ ...formData, registrationOpen: e.target.value })}
                      >
                        <MenuItem value="1">Open</MenuItem>
                        <MenuItem value="2">Post-Late</MenuItem>
                        <MenuItem value="0">Closed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Account Status</InputLabel>
                      <Select
                        value={formData.blocked || "0"}
                        label="Account Status"
                        onChange={e => setFormData({ ...formData, blocked: e.target.value })}
                      >
                        <MenuItem value="0">Active</MenuItem>
                        <MenuItem value="1">Blocked</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Security Section */}
                  <Grid item xs={12}>
                    <SectionTitle>Security</SectionTitle>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Password"
                      type="password"
                      size="small"
                      required={!isEdit}
                      value={formData.password || ''}
                      onChange={e => setFormData({ ...formData, password: e.target.value })}
                      error={!!formErrors.password}
                      helperText={formErrors.password || (isEdit ? "Leave blank to keep current password" : "")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock sx={{ fontSize: 18, color: "#9ca3af" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Access PIN"
                      size="small"
                      value={formData.accessPin || ''}
                      onChange={e => setFormData({ ...formData, accessPin: e.target.value })}
                      helperText="Optional access PIN for additional security"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Pin sx={{ fontSize: 18, color: "#9ca3af" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* System Information - Only show in edit mode */}
                  {isEdit && formData.id && (
                    <>
                      <Grid item xs={12}>
                        <SectionTitle>System Information</SectionTitle>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="ID"
                          size="small"
                          value={formData.id || ''}
                          InputProps={{ readOnly: true }}
                          disabled
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Created At"
                          size="small"
                          value={formData.createdAt ? dayjs(formData.createdAt).format("DD/MM/YYYY HH:mm") : 'N/A'}
                          InputProps={{ readOnly: true }}
                          disabled
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </DialogContent>

              <DialogActions sx={{ p: 2, borderTop: "1px solid #e5e7eb", gap: 1 }}>
                <Button onClick={() => setOpenModal(false)} variant="outlined">
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  sx={{ bgcolor: "#243b5a" }}
                >
                  {isEdit ? "Update School" : "Create School"}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </IonContent>
      </IonPage>
    </ThemeProvider>
  );
};

export default SchoolManagement;